/**
 * Add Project Task Modal
 *
 * Modal for creating new project tasks with proper form validation.
 * Styled similarly to AddBugReportModal for consistency.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProjectTaskStatus, ProjectPart } from "shared/types/renovation";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { ClerkUser } from "shared/types/user";
import { useUsers } from "shared/contexts/users.context";

interface AddProjectTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
  defaultStatus?: ProjectTaskStatus;
  defaultAssigneeId?: string;
  projectStatus?: string | null; // Current project status (maps to ProjectPhase)
}

export const AddProjectTaskModal: React.FC<AddProjectTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  defaultStatus = ProjectTaskStatus.TODO,
  defaultAssigneeId,
  projectStatus,
}) => {
  const { session } = useSession();
  const { users } = useUsers();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<ProjectTaskStatus>(defaultStatus);
  const [selectedPhase, setSelectedPhase] = useState<string | null>(
    projectStatus || null,
  );
  const [selectedAssignee, setSelectedAssignee] = useState<ClerkUser | null>(
    null,
  );
  const [selectedProjectPart, setSelectedProjectPart] = useState<number | null>(
    null,
  );
  const [projectParts, setProjectParts] = useState<ProjectPart[]>([]);
  const [isLoadingParts, setIsLoadingParts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch project parts when modal opens
  useEffect(() => {
    if (!isOpen || !session || !projectId) return;

    const fetchProjectParts = async () => {
      setIsLoadingParts(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/project/${projectId}/parts`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const parts = await response.json();
          setProjectParts(parts);
        } else {
          console.error("Failed to fetch project parts");
        }
      } catch (error) {
        console.error("Error fetching project parts:", error);
      } finally {
        setIsLoadingParts(false);
      }
    };

    fetchProjectParts();
  }, [isOpen, session, projectId]);

  // Set default assignee and phase when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset phase to project status (or null if no project status)
    setSelectedPhase(projectStatus || null);

    if (defaultAssigneeId && users && users.length > 0) {
      const defaultUser = users.find((user) => user.id === defaultAssigneeId);
      if (defaultUser && !selectedAssignee) {
        setSelectedAssignee(defaultUser);
      }
    }
  }, [isOpen, defaultAssigneeId, users, projectStatus]);

  // Update status whenever defaultStatus changes (even if modal is already open)
  useEffect(() => {
    if (!isOpen) return;
    setStatus(defaultStatus);
  }, [isOpen, defaultStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${projectId}/task`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim() || undefined,
            assignedTo: selectedAssignee?.id || undefined,
            status: status,
            projectPartId: selectedProjectPart || undefined,
            projectPhase: selectedPhase === null ? null : selectedPhase || null,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to create project task");
      }

      toast.success("Taak succesvol aangemaakt!");
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error("Error creating project task:", error);
      toast.error(
        `Fout bij aanmaken taak: ${error.message || "Onbekende fout"}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setStatus(defaultStatus);
    setSelectedPhase(projectStatus || null);
    setSelectedAssignee(null);
    setSelectedProjectPart(null);
    setProjectParts([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nieuwe Taak Toevoegen</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Titel <span className="text-red-600">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Korte beschrijving van de taak"
              required
              autoFocus
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as ProjectTaskStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ProjectTaskStatus.TODO}>Te doen</SelectItem>
                <SelectItem value={ProjectTaskStatus.IN_PROGRESS}>
                  In behandeling
                </SelectItem>
                <SelectItem value={ProjectTaskStatus.COMPLETED}>
                  Afgerond
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Project Phase */}
          <div className="space-y-2">
            <Label htmlFor="projectPhase">Projectfase</Label>
            <Select
              value={selectedPhase || "NONE"}
              onValueChange={(value) =>
                setSelectedPhase(value === "NONE" ? null : value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Geen fase</SelectItem>
                <SelectItem value="Ontwerp fase">Ontwerp</SelectItem>
                <SelectItem value="Gepland">Uitvoeringsdossier</SelectItem>
                <SelectItem value="In uitvoering">
                  In uitvoering - Werf
                </SelectItem>
                <SelectItem value="Afgerond">Voorlopige oplevering</SelectItem>
                <SelectItem value="Volledig gefactureerd">
                  Definitieve oplevering
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Project Part */}
          <div className="space-y-2">
            <Label htmlFor="projectPart">Projectonderdeel (optioneel)</Label>
            <Select
              value={selectedProjectPart?.toString() || "none"}
              onValueChange={(value) =>
                setSelectedProjectPart(
                  value === "none" ? null : parseInt(value),
                )
              }
              disabled={isLoadingParts}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoadingParts ? "Laden..." : "Selecteer projectonderdeel"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Geen projectonderdeel</SelectItem>
                {projectParts.map((part) => (
                  <SelectItem key={part.id} value={part.id.toString()}>
                    {part.title} ({part.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <Label htmlFor="assignee">Toegewezen aan</Label>
            <div className="flex items-center gap-2 rounded-md border p-2">
              <UserSelection
                defaultUser={selectedAssignee}
                onUserSelect={setSelectedAssignee}
              />
              {selectedAssignee && (
                <span className="text-sm text-neutral-600">
                  {selectedAssignee.first_name} {selectedAssignee.last_name}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Omschrijving (optioneel)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Voeg extra details toe over deze taak..."
              rows={6}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
            >
              {isSubmitting ? "Bezig..." : "Taak Toevoegen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
